import {
  DaysOfWeek,
  STATES_AND_PROVINCES,
  THE_DISTANT_FUTURE,
} from './constants';
import {
  EnforcementTimes,
  FileMetadata,
  FileType,
  FileUpload,
  GenericTypeWithID,
  Job,
  UserListItem,
  Location,
  Permit,
  RequestHeaders,
  ServiceStatus,
  StateOption,
  Status,
  Tenant,
  TimePeriod,
  TransferListArrayData,
  UnitOfTime,
  User,
} from 'types';
import {
  formatLabel,
  formatLicensePlateString,
  formatVehicleString,
} from './stringFormatters';
import AuthService from './AuthService';
import ReactGA from 'react-ga4';
import { capitalize } from '@mui/material';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import { json2csv } from 'json-2-csv';
import { ROUTES, routeWithParams } from 'router';
import * as Sentry from '@sentry/react';
import { getCookieValue } from './cookies';

export const debounce = (cb: () => void, seconds = 1) => {
  return setTimeout(cb, seconds * 1000);
};

export const toSnakeCase = (str: string): string => {
  return str
    .replace(/([a-z])([A-Z])/g, ' $1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
};

export const handleStatus = (
  status: Status,
): 'success' | 'warning' | 'error' => {
  return `${
    status === Status.Active
      ? 'success'
      : status === Status.Pending
        ? 'warning'
        : 'error'
  }`;
};

export const toTitleCase = (str: string): string => {
  if (str.indexOf('_') > 0) {
    return str
      .split('_')
      .filter((x) => x.length > 0)
      .map((x) => x.charAt(0).toUpperCase() + x.slice(1))
      .join(' ');
  }
  str = str.replace(/_/g, ' ');
  const result = str
    .replace(/([A-Z]+)/g, ' $1')
    .replace(/([A-Z][a-z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const createUUID = () => {
  return `${1e7}-${1e3}-${4e3}-${8e3}-${1e11}`.replace(/[018]/g, (c: string) =>
    (
      +c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
    ).toString(16),
  );
};

const statusMap: { [key: string]: Status } = {
  '1': Status.Active,
  '2': Status.Pending,
  '3': Status.Disabled,
};

export const StatusMap = {
  GetValue: (key: string) => {
    return statusMap[key];
  },
  GetKey: (value: Status) => {
    return Object.keys(statusMap).find((key) => statusMap[key] === value);
  },
};

export const dateToUnix = (date: string | Date): number =>
  Math.floor(new Date(date).getTime() / 1000);

export const getUnixTimestampMS = (): number => {
  return Date.now();
};

// eslint-disable-next-line
export function getValueAtPath(
  obj: any,
  path: string | string[],
  fallback?: any,
): any {
  let pathArray: string[] = [];

  if (typeof path === 'string') {
    pathArray = path.split('.');
  } else {
    path.forEach((item) => {
      const temp = item.split('.');
      pathArray = [...pathArray, ...temp];
    });
  }
  let currentValue = obj;

  for (const key of pathArray) {
    if (currentValue && currentValue[key]) {
      currentValue = currentValue[key];
    } else {
      return fallback;
    }
  }

  return currentValue;
}

export const enforcementTimesPerDay: EnforcementTimes[] = DaysOfWeek.map(
  (d) => {
    return {
      day: d,
      enforceAllDay: false,
      times: [],
    };
  },
);

// Transfer list helpers
export function createTransferListArrays<T extends GenericTypeWithID>(
  set: Array<T>,
  subset: Array<T>,
): TransferListArrayData<T> {
  const included: Array<T> = [];
  const excluded: Array<T> = [];

  set.forEach((item) => {
    if (subset.find((subItem) => subItem.id === item.id)) {
      included.push(item);
    } else {
      excluded.push(item);
    }
  });
  return {
    included,
    excluded,
  };
}
export const not = <T>(a: readonly T[], b: readonly T[]): T[] => {
  return a.filter((value) => b.indexOf(value) === -1);
};
export const intersection = <T>(a: readonly T[], b: readonly T[]): T[] => {
  return a.filter((value) => b.indexOf(value) !== -1);
};

// Get the next or previous item in an array.  Wraps around, never fails.
export const getNext = <T>(current: T, options: T[] = [], fallback: T): T => {
  const index = options.indexOf(current);
  if (index === -1) {
    return fallback;
  }
  if (index === options.length - 1) {
    return options[0];
  }
  return options[index + 1];
};
export const getPrevious = <T>(
  current: T,
  options: T[] = [],
  fallback: T,
): T => {
  const index = options.indexOf(current);
  if (index === -1) {
    return fallback;
  }
  if (index === 0) {
    return options[options.length - 1];
  }
  return options[index - 1];
};

export const hasSharedValues = (arr1: number[], arr2: number[]): boolean => {
  const set1 = new Set(arr1);
  return arr2.some((value) => set1.has(value));
};

// type guards for promises (for use with Promise.allSettled())
export const isRejected = (
  input: PromiseSettledResult<unknown>,
): input is PromiseRejectedResult => input.status === 'rejected';
export const isFulfilled = <T>(
  input: PromiseSettledResult<T>,
): input is PromiseFulfilledResult<T> => input.status === 'fulfilled';

export const getCountryByState = (stateCode: string): string => {
  const countryCode = STATES_AND_PROVINCES.find(
    (s: StateOption) => s.stateCode === stateCode,
  )?.countryCode;
  return countryCode || '';
};

export const getStateByStateCode = (stateCode: string): string => {
  const state = STATES_AND_PROVINCES.find(
    (s: StateOption) => s.stateCode === stateCode,
  );
  return state?.label || '';
};

export const getFormattedUserName = (user: User | UserListItem): string => {
  if (!user.firstName || !user.lastName) return '';
  return `${capitalize(user.firstName)} ${capitalize(user.lastName)}`;
};

export const getUserNameByID = (userID: number, users: User[]): string => {
  if (userID === 1) {
    return 'The One';
  }
  if (!users || users.length === 0) {
    return ` [ ID: ${userID} ] `;
  }
  const user = users.find((user) => user.id === userID);
  if (user) {
    return getFormattedUserName(user);
  } else {
    return ` [ ID: ${userID} ] `;
  }
};

export const addressesMatch = (
  a: Location | undefined | null,
  b: Location | undefined | null,
): boolean => {
  if (!a || !b) return false;
  return (
    a.street === b.street &&
    a.city === b.city &&
    a.postalCode === b.postalCode &&
    a.unit === b.unit
  );
};

export const getCountryFromStateCode = (stateCode: string): string => {
  const state = STATES_AND_PROVINCES.find(
    (s: StateOption) => s.stateCode === stateCode,
  );
  return state?.countryCode || '';
};

const requestHeaders: RequestHeaders = {
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
};

export function getRequestHeaders(): RequestHeaders {
  requestHeaders.headers.Authorization = 'Bearer ' + AuthService.GetToken();
  return requestHeaders;
}

const handleAuthenticationError = (errorMessage: string): void => {
  // Check if this is a permissions error (should NOT redirect to login)
  const isPermissionsError =
    /permissions.*permission denied|permission denied/i.test(errorMessage);

  if (isPermissionsError) {
    // For permissions errors, throw a special error that can be caught by React Router error boundaries
    // This will prevent navigation and allow error boundaries to show appropriate UI
    const permissionsError = new Error(`401: ${errorMessage}`);
    (permissionsError as any).isPermissionsError = true;
    throw permissionsError;
  }

  // Check if this is a token expiration error
  const isTokenExpired = /token.*expired|expired.*token/i.test(errorMessage);

  // Clear any existing token and redirect to login for authentication errors
  AuthService.LogOut();
  const companyPath = getCookieValue('companyPath');
  let loginUrl = routeWithParams(ROUTES.LOGIN, [companyPath || '']);

  // Add search parameters for toast message if token expired
  if (isTokenExpired) {
    const searchParams = new URLSearchParams();
    searchParams.set('toastType', 'info');
    searchParams.set(
      'toastMessage',
      'Your session has expired. Please log in again.',
    );
    loginUrl += `?${searchParams.toString()}`;
  }

  window.location.href = loginUrl;
};

export const handleRes = async <T>(
  res: Response,
  expectedStatus: number,
): Promise<T> => {
  // Clone response to allow reading body multiple times if needed
  const responseClone = res.clone();

  if (res.status !== expectedStatus) {
    let errorMessage = 'No error message provided';
    try {
      const data = await res.json();
      errorMessage = data.message || errorMessage;
    } catch (jsonError) {
      // Handle empty or malformed JSON responses
      try {
        const text = await responseClone.text();
        errorMessage =
          text || `HTTP ${res.status} - Unable to parse error response`;
      } catch (textError) {
        errorMessage = `HTTP ${res.status} - Unable to read response`;
      }
    }

    // Handle 401 authentication errors specially
    if (res.status === 401) {
      handleAuthenticationError(errorMessage);
      return new Promise<T>(() => {}); // Never resolves; page is redirecting
    }

    ReactGA.event({
      category: 'Error',
      action: 'Unexpected Status: ${res.status}',
      label: `URL: ${res.url}, Error: ${errorMessage}`,
    });
    throw new Error(`${res.status}: ${errorMessage}`);
  } else {
    try {
      return res.json();
    } catch (jsonError) {
      // Handle empty or malformed JSON responses for successful status codes
      try {
        const text = await responseClone.text();
        if (!text) {
          throw new Error(`${res.status}: Server returned empty response`);
        }
        throw new Error(
          `${res.status}: Invalid JSON response - ${text.substring(0, 100)}`,
        );
      } catch (textError) {
        throw new Error(`${res.status}: Unable to read response body`);
      }
    }
  }
};

export const getAllKeysFromObj = (obj: object, prefix = ''): string[] => {
  return Object.entries(obj).reduce((result, [key, value]) => {
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      result.push(...getAllKeysFromObj(value, newKey));
    } else {
      result.push(newKey);
    }
    return result;
  }, [] as string[]);
};

export const deepDiff = <T extends object>(obj1: T, obj2: T): Partial<T> => {
  const map1 = new Map(Object.entries(obj1));
  const map2 = new Map(Object.entries(obj2));
  const result = new Map();

  for (const [key, value] of map2) {
    if (map1.has(key)) {
      if (typeof value === 'object' && value !== null) {
        const nestedDiff = deepDiff(map1.get(key), value);
        if (Object.keys(nestedDiff).length > 0) {
          result.set(key, nestedDiff);
        }
      } else if (map1.get(key) !== value) {
        result.set(key, value);
      }
    } else {
      result.set(key, value);
    }
  }

  return Object.fromEntries(result);
};

export const getEntityTypeFromPath = (path: string): string => {
  const splitPath = path.split('/');

  if (splitPath.includes('revision')) {
    return splitPath[splitPath.indexOf('revision') + 1];
  }
  if (splitPath.length < 4) {
    return splitPath[1].replace(/-/g, '_').toLowerCase();
  }
  return splitPath[3].replace(/-/g, '_').toLowerCase();
};

export const getBrowserName = (userAgent: string): string => {
  // The order matters here, and this may report false positives for unlisted browsers.
  if (userAgent.includes('Firefox')) {
    // "Mozilla/5.0 (X11; Linux i686; rv:104.0) Gecko/20100101 Firefox/104.0"
    return 'Mozilla Firefox';
  } else if (userAgent.includes('SamsungBrowser')) {
    // "Mozilla/5.0 (Linux; Android 9; SAMSUNG SM-G955F Build/PPR1.180610.011) AppleWebKit/537.36 (KHTML, like Gecko) SamsungBrowser/9.4 Chrome/67.0.3396.87 Mobile Safari/537.36"
    return 'Samsung Internet';
  } else if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
    // "Mozilla/5.0 (Macintosh; Intel Mac OS X 12_5_1) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/104.0.0.0 Safari/537.36 OPR/90.0.4480.54"
    return 'Opera';
  } else if (userAgent.includes('Edge')) {
    // "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.36 Edge/16.16299"
    return 'Microsoft Edge (Legacy)';
  } else if (userAgent.includes('Edg')) {
    // "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/104.0.0.0 Safari/537.36 Edg/104.0.1293.70"
    return 'Microsoft Edge (Chromium)';
  } else if (userAgent.includes('Chrome')) {
    // "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/104.0.0.0 Safari/537.36"
    return 'Google Chrome or Chromium';
  } else if (userAgent.includes('Safari')) {
    // "Mozilla/5.0 (iPhone; CPU iPhone OS 15_6_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/15.6 Mobile/15E148 Safari/604.1"
    return 'Apple Safari';
  } else {
    return 'unknown';
  }
};

export const isValidISODateTime = (dateTime: string): boolean => {
  const regex =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(\.\d+)?(([+-]\d{2}:\d{2})|Z)?$/;
  return regex.test(dateTime);
};

export const startsWithNumber = (s: string): boolean => {
  return /^\d/.test(s);
};

export const getCameraPermission = async (): Promise<boolean> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
    });
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch (error) {
    return false;
  }
};

export function getThemeColorForStatus(
  status: ServiceStatus,
): 'success' | 'warning' | 'error' | 'disabled' {
  switch (status) {
    case ServiceStatus.PASS:
      return 'success';
    case ServiceStatus.WARN:
      return 'warning';
    case ServiceStatus.FAIL:
      return 'error';
    case ServiceStatus.OFFLINE:
      return 'disabled';
  }
}

type CreateFileParams = {
  imageSrc: string;
  metadata: FileMetadata;
  isUploaded: boolean;
};
export function createFile({
  imageSrc,
  metadata,
  isUploaded,
}: CreateFileParams): FileUpload {
  // Convert base64 to raw binary data held in a string
  let byteString;
  if (imageSrc.split(',')[0].indexOf('base64') >= 0) {
    byteString = atob(imageSrc.split(',')[1]);
  } else {
    byteString = unescape(imageSrc.split(',')[1]);
  }
  // Separate out the mime component
  const mimeString = imageSrc.split(',')[0].split(':')[1].split(';')[0];
  // Write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([ia], { type: mimeString });
  const file = new File([blob], metadata.data?.title || `image-${Date.now()}`, {
    type: 'image/png',
  });
  const fileWithMetadata: FileUpload = Object.assign(file, {
    preview: URL.createObjectURL(file),
    metadata: metadata,
    uploaded: isUploaded,
    base64: imageSrc.split('data:image/png;base64,')[1],
  });
  return fileWithMetadata;
}

export const getMostRecentStatus = (job: Job | undefined): string => {
  if (!job?.data) return '';
  let statusText;

  if (!job.data.statuses || job.data.statuses.length === 0) {
    statusText = 'requested';
  } else {
    const index = job.data.statuses.length - 1;
    const status = job.data.statuses[index];
    statusText = status.status;
  }
  return `${formatLabel(statusText)}`;
};

export const getFileTypeForFile = (file: File): FileType => {
  const fileType = file.type.split('/')[0];
  switch (fileType) {
    case 'image':
      return FileType.Image;
    case 'video':
      return FileType.Video;
    case 'application':
      return FileType.Document;
    default:
      return FileType.Other;
  }
};

export const generateCSV = (
  items: Job[] | Permit[],
  filename: string,
  type: 'permit' | 'job',
): void => {
  let temp: object[] = [];
  if (type === 'permit') {
    items.forEach((i) => {
      temp.push({
        'Permit ID': i.id,
        Created: dayjs(i.updated).format('LLL'),
        Updated: dayjs(i.updated).format('LLL'),
        'Invite Status': (i as Permit).inviteStatus,
        'Permit Status': (i as Permit).permitStatus,
        'Permit Number': (i as Permit).permitNumber,
        'Start Time': dayjs((i as Permit).startTime).format('LLL'),
        'End Time': dayjs((i as Permit).endTime).format('LLL'),
        Vehicle: formatVehicleString((i as Permit).vehicle),
        Contact: `${(i as Permit).contact?.name} - ${
          (i as Permit).contact?.primaryEmail || 'No Email'
        }`,
      });
    });
  } else {
    temp = [...items];
  }
  const csvString = json2csv(temp, { expandNestedObjects: true });
  const link = document.createElement('a');
  link.setAttribute(
    'href',
    'data:text/plain;charset=utf-8,' + encodeURIComponent(csvString),
  );
  link.setAttribute('download', filename);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const generateJobPDF = (
  jobs: Job[],
  filename: string,
  tenant: Tenant,
): void => {
  const doc = new jsPDF('landscape', 'px');
  const array: {
    [key: string]: string;
  }[] = [];
  jobs.forEach((j) => {
    const o: any = {};
    for (const [key, value] of Object.entries(j)) {
      switch (key) {
        case 'id':
          o[key] = String(value) || ' ';
          break;
        case 'type':
          o[key] = value || ' ';
          break;
        case 'status':
          o[key] = value || ' ';
          break;
        case 'propertyName':
          o[key] = value || ' ';
          break;
        case 'createdByUserName':
          o[key] = value || ' ';
          break;
        case 'requestTime':
          o[key] = dayjs(j.requestTime).format('LLL') || ' ';
          break;
        case 'vehicle':
          o['year'] = String(value.year) || ' ';
          o['make'] = value.make || ' ';
          o['model'] = value.model || ' ';
          o['color'] = value.color || ' ';
          o['trim'] = value.trim || ' ';
          o['vin'] = value.vin || ' ';
          break;
        case 'plate':
          o['plate'] = formatLicensePlateString(value) || ' ';
          break;
      }
    }
    array.push(o);
  });
  doc.setLineWidth(0.2);
  doc.setFontSize(10);
  doc.setDisplayMode('fullpage');
  doc.table(
    5,
    5,
    array,
    [
      {
        name: 'id',
        prompt: 'Job ID',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'type',
        prompt: 'Type',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'status',
        prompt: 'Status',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'propertyName',
        prompt: 'Property',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'createdByUserName',
        prompt: 'Created By',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'requestTime',
        prompt: 'Request Time',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'year',
        prompt: 'Year',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'make',
        prompt: 'Make',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'model',
        prompt: 'Model',
        align: 'left',
        padding: 1,
        width: 600,
      },
      {
        name: 'color',
        prompt: 'Color',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'trim',
        prompt: 'Trim',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'vin',
        prompt: 'VIN',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'plate',
        prompt: 'Plate',
        align: 'left',
        padding: 1,
        width: 0,
      },
    ],
    {
      printHeaders: true,
      headerBackgroundColor: tenant.data.branding?.primary,
      margins: 0,
      padding: 6,
      autoSize: true,
      css: {
        'font-size': 0.5,
      },
    },
  );
  doc.save(filename);
};

export const generatePermitPDF = (
  permits: Permit[],
  filename: string,
  tenant: Tenant,
): void => {
  const doc = new jsPDF('landscape', 'px');
  const array: {
    [key: string]: string;
  }[] = [];
  permits.forEach((p) => {
    const o: any = {};
    for (const [key, value] of Object.entries(p)) {
      switch (key) {
        case 'id':
          o[key] = String(value) || ' ';
          break;
        case 'created':
          o[key] = dayjs(p.created).format('LLL') || ' ';
          break;
        case 'updated':
          o[key] = dayjs(p.updated).format('LLL') || ' ';
          break;
        case 'inviteStatus':
          o[key] = value || ' ';
          break;
        case 'permitStatus':
          o[key] = value || ' ';
          break;
        case 'permitNumber':
          o[key] = value || ' ';
          break;
        case 'startTime':
          o[key] = dayjs(p.startTime).format('LLL') || ' ';
          break;
        case 'endTime':
          o[key] = dayjs(p.endTime).format('LLL') || ' ';
          break;
        case 'vehicle':
          o[key] = formatVehicleString(value) || ' ';
          break;
        case 'contact':
          o[key] =
            `${value.name} - ${value.primaryEmail || 'No Email'} ` || ' ';
          break;
      }
    }
    array.push(o);
  });
  doc.setLineWidth(0.1);
  doc.setFontSize(3);
  doc.setDisplayMode('fullpage');
  doc.table(
    5,
    5,
    array,
    [
      {
        name: 'id',
        prompt: 'Permit ID',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'created',
        prompt: 'Created',
        align: 'left',
        padding: 1,
        width: 3,
      },
      {
        name: 'updated',
        prompt: 'Updated',
        align: 'left',
        padding: 1,
        width: 3,
      },
      {
        name: 'inviteStatus',
        prompt: 'Invite Status',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'permitStatus',
        prompt: 'Permit Status',
        align: 'left',
        padding: 1,
        width: 1,
      },
      {
        name: 'permitNumber',
        prompt: 'Permit Number',
        align: 'left',
        padding: 1,
        width: 0,
      },
      {
        name: 'startTime',
        prompt: 'Start Time',
        align: 'left',
        padding: 1,
        width: 3,
      },
      {
        name: 'endTime',
        prompt: 'End Time',
        align: 'left',
        padding: 1,
        width: 3,
      },
      {
        name: 'vehicle',
        prompt: 'Vehicle',
        align: 'left',
        padding: 1,
        width: 4,
      },
      {
        name: 'contact',
        prompt: 'Contact Name and Email',
        align: 'left',
        padding: 1,
        width: 3,
      },
    ],
    {
      printHeaders: true,
      headerBackgroundColor: tenant.data.branding?.primary,
      margins: 0.5,
      padding: 1.5,
      autoSize: true,
      css: {
        'font-size': 0.3,
      },
    },
  );
  doc.save(filename);
};

export const calculateEndOfTimePeriod = (
  startTime: Date | number | undefined,
  term: TimePeriod | undefined,
): Date => {
  if (!startTime || !term || term.amountOfTime < 1) {
    return new Date(THE_DISTANT_FUTURE);
  }
  const startTimeUnix =
    typeof startTime === 'number' ? startTime : startTime.getTime();

  switch (term.unitOfTime) {
    case UnitOfTime.Minute:
      return new Date(startTimeUnix + term.amountOfTime * 60000);
    case UnitOfTime.Hour:
      return new Date(startTimeUnix + term.amountOfTime * 3600000);
    case UnitOfTime.Day:
      return new Date(startTimeUnix + term.amountOfTime * 86400000);
    case UnitOfTime.Week:
      return new Date(startTimeUnix + term.amountOfTime * 604800000);
    case UnitOfTime.Month:
      return new Date(startTimeUnix + term.amountOfTime * 2592000000);
    case UnitOfTime.Year:
      return new Date(startTimeUnix + term.amountOfTime * 31536000000);
    default:
      return new Date(THE_DISTANT_FUTURE);
  }
};

export const getCalcTitleFragments = (title: string): [string, string] => {
  const pieces = title.split(' ');
  let firstPiece = '';
  let secondPiece = '';

  switch (pieces.length) {
    case 1:
      firstPiece = pieces[0];
      break;
    case 2:
      firstPiece = pieces[0];
      secondPiece = pieces[1];
      break;
    case 3: {
      // figure out where to put the break
      const big = Math.max(
        pieces[0].length,
        pieces[1].length,
        pieces[2].length,
      );
      if (big === pieces[0].length) {
        firstPiece = pieces[0];
        secondPiece = pieces[1] + ' ' + pieces[2];
      } else if (big === pieces[1].length) {
        firstPiece = pieces[0] + ' ' + pieces[1];
        secondPiece = pieces[2];
      } else {
        firstPiece = pieces[0] + ' ' + pieces[1];
        secondPiece = pieces[2];
      }
      break;
    }
    case 4:
      firstPiece = pieces[0] + ' ' + pieces[1];
      secondPiece = pieces[2] + ' ' + pieces[3];
      break;
    default:
      firstPiece = pieces.slice(0, 2).join(' ');
      secondPiece = pieces.slice(2).join(' ');
      break;
  }

  return [firstPiece, secondPiece];
};

export const getAnonymizedPath = (pathname: string): string => {
  // Remove trailing slash if present
  const cleanPath = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  // Split path into segments
  const segments = cleanPath.split('/');

  // Find matching route pattern
  const matchingRoute = Object.values(ROUTES).find((route: string) => {
    const routeSegments = route.split('/');
    if (routeSegments.length !== segments.length) return false;

    return routeSegments.every((routeSeg, i) => {
      // If route segment starts with :, it's a parameter
      if (routeSeg.startsWith(':')) return true;
      return routeSeg === segments[i];
    });
  });

  return matchingRoute || pathname;
};

export const logError = (error: Error | string) => {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.error(error);
  }

  if (error instanceof Error) {
    Sentry.captureException(error, {
      level: 'error',
    });
  } else {
    Sentry.captureMessage(error, {
      level: 'error',
    });
  }
};

/**
 * Normalizes a license plate number by removing all non-alphanumeric characters.
 * This ensures consistent comparison regardless of spaces, dashes, or other formatting.
 *
 * @param plateNumber - The license plate number to normalize
 * @returns The normalized plate number containing only alphanumeric characters
 *
 * @example
 * normalizePlateNumber("ABC-123") // returns "ABC123"
 * normalizePlateNumber("ABC 123") // returns "ABC123"
 * normalizePlateNumber("ABC_123") // returns "ABC123"
 */
export const normalizePlateNumber = (plateNumber: string): string => {
  if (!plateNumber) return '';
  return plateNumber.replace(/[^a-zA-Z0-9]/g, '');
};

const CANCEL_BLOCKED_STATUSES: Record<string, string[]> = {
  tow: ['complete', 'cancelled', 'denied', 'approvaldenied', 'dropped'],
  tag: [
    'enforced',
    'expired',
    'cancelled',
    'denied',
    'approvaldenied',
    'removed',
  ],
};

const TOW_STATUS_CANCELLATION_RANK: Record<string, number> = {
  pendingapproval: 0,
  requested: 1,
  received: 2,
  acknowledged: 3,
  enroute: 1,
  arrived: 4,
  hooked: 5,
};

export const canJobBeCancelled = (
  job: Job | null,
  beforeStatus?: string,
): boolean => {
  if (!job || !beforeStatus) return false;
  
  const cleanBeforeStatus = String(beforeStatus).replace(/[^a-zA-Z]/g, '').toLowerCase();

  const blocked = CANCEL_BLOCKED_STATUSES[job.type];
  if (!blocked) return false;

  const status = String(job.status).replace(/[^a-zA-Z]/g, '').toLowerCase();
  if (blocked.includes(status)) return false;
  if (job.type === 'tow') {
    return (
      (TOW_STATUS_CANCELLATION_RANK[status] || 0) <
      (TOW_STATUS_CANCELLATION_RANK[cleanBeforeStatus] || 0)
    );
  }
  return true;
};

export * from './AuthService';
export * from './constants';
export * from './cookies';
export * from './permissions';
export * from './stringFormatters';
export * from './styleProps';
export * from './validators';
export * from 'context/themeContext';
export * from './emptyEntities';
